import { BarChart, Card, EmptyState, Skeleton } from '@bbpos/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { dashboardService } from '@/services/dashboard.service';
import { useDashboardStore, resolvePeriod } from '@/stores/use-dashboard-store';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useStoreTimezone } from '@/lib/store-clock';
import type { CurrencyCode } from '@bbpos/types';

/**
 * Top categories by revenue for the currently selected location and today's
 * date on that store's clock (T18). Driven by /dashboard/top-categories — groups by
 * products.category_id (preferred) or the legacy products.category string.
 *
 * Replaces the previous drill-down('CATEGORY', 'ALL') misuse that returned
 * no rows and rendered an empty bar chart.
 *
 * FR-DASH-003 / QA 8.7.5 — tapping a category drills in: the row expands
 * with that category's revenue, bills and products, from
 * /dashboard/drill-down filtered on the same key this widget groups by.
 */
export function TopCategoriesWidget() {
  const locationId = useDashboardStore((s) => s.selectedLocationId);
  const period = useDashboardStore((s) => s.period);
  const { startDate, endDate } = resolvePeriod(period, useStoreTimezone(locationId));
  const currency = useTenantCurrency();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'top-categories', locationId, startDate, endDate],
    queryFn: () =>
      dashboardService.getTopCategories({ locationId, startDate, endDate, limit: 5 }),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <Skeleton variant="text" width="40%" height={16} />
        <div className="mt-3">
          <Skeleton variant="rect" height={180} />
        </div>
      </Card>
    );
  }

  const categories = data?.categories ?? [];

  if (categories.length === 0) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
          Top Categories
        </h3>
        <EmptyState
          variant="inline"
          title="No category sales yet"
          description="Complete a sale to see category breakdown"
        />
      </Card>
    );
  }

  const chartData = categories.map((c) => ({
    label: c.name,
    value: c.revenue,
  }));

  return (
    <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg animate-slide-up">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          Top Categories
        </h3>
        <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 tabular-nums">
          Today · {formatPaise(data?.total_revenue ?? 0, currency)}
        </span>
      </div>
      <BarChart
        data={chartData}
        valueFormatter={(paise) => formatPaise(paise, currency)}
      />
      <div className="mt-3 space-y-1.5">
        {categories.map((c) => (
          <div key={c.key}>
            <button
              type="button"
              aria-expanded={openKey === c.key}
              onClick={() => setOpenKey(openKey === c.key ? null : c.key)}
              className="w-full flex items-center justify-between text-xs rounded-md px-1 py-0.5 hover:bg-brand-50 dark:hover:bg-neutral-800 transition-all duration-200"
            >
              <span className="truncate text-neutral-700 dark:text-neutral-200">{c.name}</span>
              <span className="ml-2 shrink-0 font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                {formatPaise(c.revenue, currency)} · {c.share_pct}%
              </span>
            </button>
            {openKey === c.key && (
              <CategoryDrillDown
                categoryKey={c.key}
                locationId={locationId}
                startDate={startDate}
                endDate={endDate}
                currency={currency}
              />
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function CategoryDrillDown({
  categoryKey,
  locationId,
  startDate,
  endDate,
  currency,
}: {
  categoryKey: string;
  locationId: string | undefined;
  startDate: string;
  endDate: string;
  currency: CurrencyCode;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', 'drill-down', 'CATEGORY', categoryKey, locationId, startDate, endDate],
    queryFn: () =>
      dashboardService.getDrillDown({
        dimension: 'CATEGORY',
        value: categoryKey,
        locationId,
        startDate,
        endDate,
        limit: 10,
      }),
    staleTime: 30_000,
  });

  if (isLoading) {
    return <Skeleton variant="rect" height={48} className="mt-1" />;
  }
  if (isError || !data) {
    return <p className="mt-1 px-1 text-[11px] text-danger-600">Could not load this category.</p>;
  }

  const { summary, details } = data.data;
  return (
    <div className="mt-1 mb-2 rounded-lg border border-brand-100 dark:border-neutral-700 bg-brand-50/60 dark:bg-neutral-800/60 p-2 animate-fade-in">
      <p className="text-[11px] text-neutral-600 dark:text-neutral-300 tabular-nums">
        {formatPaise(summary.revenue, currency)} · {summary.bill_count} bills
      </p>
      {details.length === 0 ? (
        <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">No products sold.</p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {details.map((d) => (
            <li key={d.product_id} className="flex items-center justify-between text-[11px]">
              <span className="truncate text-neutral-700 dark:text-neutral-200">{d.product_name}</span>
              <span className="ml-2 shrink-0 font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                {Number(d.quantity_sold)} × · {formatPaise(d.revenue, currency)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
